import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Collapse,
  Grid,
} from "@mui/material";
import { useTable, useSortBy, useFilters, usePagination } from "react-table";
import { CSVLink } from "react-csv";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { authService } from "../../utils/authService";
import { useNavigate } from "react-router-dom";
import { ExpandedRowContent } from "./expandedRowContent";
import { getTableColumns } from "./utils/clientUtils";
import { exportToPDFClients } from "./utils/exportPdfClients";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

function ClientList() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const navigate = useNavigate();
  const isAdmin = authService.getUserRole() === "admin";

  // Memoriza la función para evitar que se vuelva a crear en cada render
  const toggleRowExpansion = useCallback((rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  }, []);

  // Memoriza la función de edición
  const handleEdit = useCallback(
    async (client) => {
      const response = await axiosInstance.get(`/clients/${client.id}`);
      navigate("/client-management", {
        state: {
          client: response.data,
          editMode: true,
        },
      });
    },
    [navigate]
  );

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClients = async () => {
    try {
      const token = authService.getToken();
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/clients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Incluimos todas las dependencias necesarias en useMemo
  const columns = useMemo(
    () => getTableColumns(toggleRowExpansion, expandedRows, handleEdit, isAdmin, t),
    [toggleRowExpansion, expandedRows, handleEdit, isAdmin, t]
  );

  const filteredData = useMemo(
    () =>
      clients.filter(
        (client) =>
          client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.phone.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [clients, searchTerm]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex, pageSize },
    setPageSize,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 10 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  return (
    <Box sx={{ height: "100%", width: "100%", p: 2 }}>
      <Grid container spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            placeholder={t("searchClients")}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: "300px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} display="flex" gap={1} justifyContent="flex-end">
          <CSVLink data={filteredData} filename={"clients.csv"} style={{ textDecoration: "none" }}>
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              {t("exportCSV")}
            </Button>
          </CSVLink>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToPDFClients(filteredData)}
            sx={{ height: "37px" }}
          >
            {t("exportPDF")}
          </Button>
        </Grid>

        <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/client-management")}
          >
            {t("createClient")}
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <table
            {...getTableProps()}
            style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "white",
            }}
          >
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={{
                        padding: "16px",
                        background: "#f5f5f5",
                        fontWeight: "bold",
                        textAlign: "left",
                        borderBottom: "2px solid #e0e0e0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.getRowProps().key}>
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          style={{
                            padding: "16px",
                            borderBottom: "1px solid #f0f0f0",
                            textAlign: "left",
                          }}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.original.id] && (
                      <tr>
                        <td colSpan={columns.length}>
                          <Collapse in={expandedRows[row.original.id]}>
                            <ExpandedRowContent data={row.original} fetchClients={fetchClients} />
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  {t("show")} {size}
                </option>
              ))}
            </select>
            <Box>
              {t("page")} {pageIndex + 1} {t("of")} {pageOptions.length}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button onClick={() => previousPage()} disabled={!canPreviousPage} variant="outlined">
              {t("previous")}
            </Button>
            <Button onClick={() => nextPage()} disabled={!canNextPage} variant="outlined">
              {t("next")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default ClientList;
